import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

const ENTER_KEY = 13
// The chevrons' natural display is `inline-block`, which is what jQuery's .hide()/.show() pair
// recorded and restored; setting it explicitly keeps that.
const ARROW_SHOWN = "inline-block"
const ARROW_HIDDEN = "none"

export default class extends Controller {
  static get targets() { return ["ageCheck", "submitButton", "alternateImages"]; }
  static get values() { return { real: Boolean, redemptionToken: String }; }

  connect() {
    // The image the page arrived with, not whatever the chevrons have paged to since.
    this.openingImageSrc = this.displayedImage() && this.displayedImage().getAttribute("src")

    this.preloadVariantImages()
    this.preloadAlternateImages()

    window.ltFavoriteHelpers.initialize(this.redemptionTokenValue)

    this.syncSubmit()
  }

  syncSubmit() {
    if (!this.hasAgeCheckTarget || !this.hasSubmitButtonTarget) { return; }

    this.submitButtonTarget.disabled = !this.ageCheckTarget.checked
  }

  toggleFavorite(event) {
    // `remove_element` is false on this page: only the My Favorites grid takes the tile away.
    window.ltFavoriteHelpers.toggleFavorite(
      Number(event.currentTarget.value), this.redemptionTokenValue, false, this.realValue
    )
  }

  changeVariantImage(event) {
    const path = this.variantImages()[event.currentTarget.value.toLowerCase()]
    const showArrows = !path

    // div.arrow and img.displayed-image come from ProductDecorator#image_link/#image_tag, shared
    // by product-showcase, catalogs, admin-popular-redemptions and the gives collection modals --
    // a target attribute here would be inert noise on every mount but this one (D12). Stays a
    // scoped query.
    this.element.querySelectorAll("div.arrow").forEach(arrow => {
      arrow.style.display = showArrows ? ARROW_SHOWN : ARROW_HIDDEN
    })
    this.element.querySelectorAll("img.displayed-image").forEach(image => {
      image.setAttribute("src", path || this.openingImageSrc)
    })
  }

  changeProductImage(event) {
    event.preventDefault()

    // Both edit actions render the bare partial only for a request that looks like AJAX.
    const options = RequestHelper.defaultRequestOptions()
    options.headers = Object.assign({}, options.headers, { "X-Requested-With": "XMLHttpRequest" })

    fetch(event.currentTarget.dataset.path, options).
      then(response => {
        if (!response.ok) { throw new Error(`Product image lookup failed (${response.status})`); }
        return response.text()
      }).
      // Server-rendered from our own `unties/products/_product_image` partial (S1: same origin,
      // no unsanitized user input in the response), and it replaces the arrows/image wholesale on
      // every page, so those elements can't be declared Targets (P8) -- they're queried fresh here.
      then(html => { this.element.querySelector(".product-image").outerHTML = html }).
      catch(this.logFailedCall)
  }

  changeProductImageOnEnter(event) {
    if (event.which !== ENTER_KEY) { return; }

    this.changeProductImage(event)
  }

  // Shared decorator-generated markup (see changeVariantImage) -- not a Target, D12.
  displayedImage() {
    return this.element.querySelector("img.displayed-image")
  }

  // Keyed by variant id, with the product's own image as each variant's fallback, so an entry is
  // missing only for the blank "Select One". Not a Target: the whole dataset is the map, so a target
  // attribute on the holder would become an entry and get preloaded as a URL.
  variantImages() {
    const holder = this.element.querySelector(".variant-images")

    return holder ? holder.dataset : {}
  }

  preloadVariantImages() {
    Object.values(this.variantImages()).forEach(url => { new Image().src = url })
  }

  preloadAlternateImages() {
    if (!this.hasAlternateImagesTarget) { return; }

    // `preload` is an array attribute (ProductDecorator#image_links via `data: {preload: ...}`),
    // rendered as a JSON string; jQuery's `.data()` used to auto-parse it, so `JSON.parse` here
    // replicates that instead of assuming the two accessors are interchangeable (pattern 6).
    JSON.parse(this.alternateImagesTarget.dataset.preload).forEach(url => { new Image().src = url })
  }

  logFailedCall(response) {
    console.warn(response)
  }
}
